import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import MenuItem from '@mui/material/MenuItem';
import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { endEmployment } from 'src/redux/slices/employees';

import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import { IEmployeeItem, EMPLOYMENT_END_REASON_OPTIONS } from 'src/types/employee';

interface IEndEmploymentDialog {
  employee: IEmployeeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

const OTHER_REASON_VALUE = 'other';

function EndEmploymentDialog({ employee, isOpen, onClose, onSuccess }: IEndEmploymentDialog) {
  const dispatch = useAppDispatcher();

  const endEmploymentSchema = useMemo(
    () =>
      Yup.object().shape({
        employmentEndAt: Yup.date()
          .required('Employment end date is required')
          .max(new Date(), 'Employment end date cannot be in the future'),
        reason: Yup.string().required('Reason is required'),
        otherReason: Yup.string().when('reason', {
          is: OTHER_REASON_VALUE,
          then: (schema) => schema.required('Please specify a reason'),
        }),
      }),
    []
  );

  const defaultValues = useMemo(
    () => ({
      employmentEndAt: new Date(),
      reason: '',
      otherReason: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(endEmploymentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
    reset,
  } = methods;

  const reason = watch('reason');

  const onSubmit = handleSubmit(async (data) => {
    try {
      const selectedOption = EMPLOYMENT_END_REASON_OPTIONS.find(
        (option) => option.value === data.reason
      );

      const employmentEndReason =
        data.reason === OTHER_REASON_VALUE
          ? data.otherReason ?? ''
          : selectedOption?.label ?? '';

      await dispatch(
        endEmployment({
          employeeId: employee._id,
          employmentEndAt: new Date(data.employmentEndAt),
          employmentEndReason,
        })
      ).unwrap();

      enqueueSnackbar('Employment ended successfully');

      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message || 'Failed to end employment', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>End Employment</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Set the effective end date and reason for ending{' '}
            <strong>
              {employee.firstName}
              {employee.lastName ? ` ${employee.lastName}` : ''}
            </strong>
            &apos;s employment.
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="employmentEndAt"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Employment End Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            <RHFSelect
              required
              name="reason"
              label="Reason"
              SelectProps={{
                renderValue: (value) =>
                  EMPLOYMENT_END_REASON_OPTIONS.find((o) => o.value === value)?.label ?? '',
              }}
            >
              {EMPLOYMENT_END_REASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {option.labelSi}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </RHFSelect>

            {reason === OTHER_REASON_VALUE && (
              <RHFTextField
                required
                name="otherReason"
                label="Please specify a reason"
                multiline
                rows={3}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" color="error" loading={isSubmitting}>
            End Employment
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default EndEmploymentDialog;
